import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Ban, Filter, AlertCircle, AlertTriangle, MousePointer, TrendingDown, X, MinusCircle, Check, Loader2, Settings } from 'lucide-react';
import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

// Paramètres par défaut
const DEFAULT_SETTINGS = {
  period: 'LAST_90_DAYS',
  minClicks: 1,
  minCost: 0,
  campaignType: 'ALL',
  aggregateByCampaign: true
};

// Nom de l'outil pour la sauvegarde des paramètres
const TOOL_NAME = 'microsoftNegativeKeywords';

const MicrosoftNegativeKeywordsTool = ({ token, selectedClientId, updateUrlWithAccount }) => {
  // États pour les comptes
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // États pour les données
  const [searchTermsData, setSearchTermsData] = useState([]);
  const [existingNegatives, setExistingNegatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // État pour le modal de détails
  const [selectedTerm, setSelectedTerm] = useState(null);

  // États pour le modal d'exclusion
  const [excludeTerm, setExcludeTerm] = useState(null);
  const [editedExcludeTerm, setEditedExcludeTerm] = useState('');
  const [excludeMatchType, setExcludeMatchType] = useState('Exact');
  const [excludeTargetType, setExcludeTargetType] = useState('campaign');
  const [excludeSelectedTargets, setExcludeSelectedTargets] = useState([]);
  const [sharedLists, setSharedLists] = useState([]);
  const [loadingSharedLists, setLoadingSharedLists] = useState(false);
  const [selectedSharedList, setSelectedSharedList] = useState('');
  const [newListName, setNewListName] = useState('');
  const [excludeLoading, setExcludeLoading] = useState(false);
  const [excludeError, setExcludeError] = useState(null);
  const [excludeSuccess, setExcludeSuccess] = useState(null);

  // États pour les paramètres
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [tierFilter, setTierFilter] = useState(null);

  // Pagination
  const ITEMS_PER_PAGE = 50;
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);

  // Refs pour annuler les requêtes et ignorer les résultats obsolètes
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  // Charger les paramètres depuis l'API quand le compte change
  useEffect(() => {
    const loadSettings = async () => {
      if (!token || !selectedAccount) {
        setSettings(DEFAULT_SETTINGS);
        setSettingsLoaded(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success && response.data.settings) {
          setSettings({ ...DEFAULT_SETTINGS, ...response.data.settings });
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      } catch (e) {
        console.error('Erreur chargement paramètres:', e);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadSettings();
  }, [token, selectedAccount]);

  // Sauvegarder les paramètres dans l'API quand ils changent
  useEffect(() => {
    if (!settingsLoaded || !token || !selectedAccount) return;

    const saveSettings = async () => {
      try {
        await axios.post(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`,
          { settings },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (e) {
        console.error('Erreur sauvegarde paramètres:', e);
      }
    };

    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [settings, settingsLoaded, token, selectedAccount]);

  // Vérifier si les paramètres sont personnalisés
  const hasCustomSettings = useMemo(() => {
    return Object.keys(DEFAULT_SETTINGS).some(key => {
      return String(DEFAULT_SETTINGS[key]) !== String(settings[key]);
    });
  }, [settings]);

  // État pour le tri
  const [sortColumn, setSortColumn] = useState('clicks');
  const [sortDirection, setSortDirection] = useState('desc');

  // Charger les comptes au montage et quand le client change
  useEffect(() => {
    if (!token) return;

    // Incrémenter le requestId pour invalider toutes les requêtes en cours
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    // Annuler toutes les requêtes en cours
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Réinitialiser tous les états quand le client change
    setSelectedAccount('');
    setSearchTermsData([]);
    setLoading(false);
    setError(null);
    setSelectedTerm(null);
    setSettingsLoaded(false);

    // Recharger les comptes
    loadAccounts(currentRequestId);
  }, [token, selectedClientId]);

  // Charger les données quand le compte est sélectionné (sans dépendance sur les settings)
  useEffect(() => {
    if (token && selectedAccount) {
      fetchData();
    }
  }, [token, selectedAccount]);

  // Réinitialiser la pagination quand les filtres ou les données changent
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [tierFilter, settings.minClicks, settings.minCost, settings.campaignType, selectedAccount, searchTermsData]);

  const loadAccounts = async (currentRequestId) => {
    // Utiliser le requestId courant si non fourni
    const reqId = currentRequestId || requestIdRef.current;

    try {
      setLoadingAccounts(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await axios.get(`${API_URL}/api/library?usage=outils`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });

      // Vérifier si cette requête est encore valide
      if (reqId !== requestIdRef.current) {
        return;
      }

      if (response.data.success && response.data.library) {
        const data = response.data;
        const microsoftAdsItems = data.library?.MICROSOFT_ADS || [];

        const adsItems = [
          ...microsoftAdsItems.filter(item => item.type === 'MICROSOFT_ADS')
        ];

        let filteredAccounts = adsItems;
        if (selectedClientId) {
          filteredAccounts = await filterAccountsByClient(adsItems, selectedClientId, reqId);
          // Re-vérifier après l'appel async
          if (reqId !== requestIdRef.current) {
            return;
          }
        }

        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filteredAccounts].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        if (sortedAccounts.length === 1) {
          const firstAccount = sortedAccounts[0];
          const accountId = firstAccount.sourceId || firstAccount.id;
          setSelectedAccount(accountId);
        }
      }
    } catch (error) {
      // Ignorer les erreurs d'annulation
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      if (reqId !== requestIdRef.current) return;
      console.error('Erreur chargement comptes:', error);
      setError('Impossible de charger les comptes Microsoft Ads');
    } finally {
      if (reqId === requestIdRef.current) {
        setLoadingAccounts(false);
      }
    }
  };

  const filterAccountsByClient = async (accounts, clientId) => {
    if (!clientId) return accounts;

    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.sites) {
        const clientSites = data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);

        return accounts.filter(account => {
          return authorizedAccountIds.includes(account.id) ||
                 authorizedAccountIds.includes(account.sourceId);
        });
      }
    } catch (err) {
      console.error('Erreur filtrage comptes:', err);
    }
    return accounts;
  };

  const fetchData = async () => {
    // Incrémenter le requestId pour cette nouvelle requête
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    // Créer un AbortController pour cette requête (sera annulé uniquement si le client change)
    // IMPORTANT: Ne pas annuler le précédent controller ici - on ignore juste les réponses obsolètes avec requestIdRef
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      let searchTermsData = [];

      // Toujours utiliser l'API temps réel pour l'outil (le cache est réservé au widget homepage)
      const searchTermsResponse = await axios.get(`${API_URL}/api/microsoft-keyword-opportunities/search-terms-all`, {
        params: {
          clientId: selectedAccount,
          period: settings.period,
          aggregateByCampaign: settings.aggregateByCampaign,
          minClicks: settings.minClicks
        },
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });

      // Vérifier si cette requête est encore valide
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      searchTermsData = searchTermsResponse.data;
      setSearchTermsData(searchTermsData);

      // Récupérer les mots-clés négatifs existants
      const keywordsResponse = await axios.get(`${API_URL}/api/microsoft-keyword-opportunities/existing-keywords`, {
        params: { clientId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });

      // Re-vérifier après le second appel
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      // Filtrer pour ne garder que les négatifs
      const allKeywords = keywordsResponse.data.keywords || keywordsResponse.data || [];
      const negatives = allKeywords.filter(k => k.isNegative);
      setExistingNegatives(negatives);

    } catch (err) {
      // Ignorer les erreurs d'annulation (quand le client change)
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      // Ignorer les erreurs si la requête est obsolète
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      console.error('Erreur chargement termes de recherche:', err.message);
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
    } finally {
      // Ne mettre à jour loading que si la requête est encore valide
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Charger les listes partagées
  const loadSharedLists = async () => {
    if (!selectedAccount) return;

    setLoadingSharedLists(true);
    try {
      const response = await axios.get(`${API_URL}/api/microsoft-keyword-opportunities/shared-lists`, {
        params: { clientId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setSharedLists(response.data.sharedLists || []);
      }
    } catch (err) {
      console.error('Erreur chargement listes partagées:', err);
    } finally {
      setLoadingSharedLists(false);
    }
  };

  // Ouvrir le modal d'exclusion
  const openExcludeModal = (term) => {
    // Effacer toute sélection de texte
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    }

    setExcludeTerm(term);
    setEditedExcludeTerm(term.searchTerm);
    setExcludeMatchType('EXACT');
    setExcludeTargetType('campaign');
    setExcludeError(null);
    setExcludeSuccess(null);
    setSelectedSharedList('');
    setNewListName('');

    // Pré-sélectionner les cibles depuis le breakdown
    if (term.breakdown && term.breakdown.length > 0) {
      // Par défaut, pré-cocher toutes les campagnes du breakdown
      const campaignIds = [...new Set(term.breakdown.map(b => b.campaignId).filter(Boolean))];
      setExcludeSelectedTargets(campaignIds);
    } else {
      setExcludeSelectedTargets([]);
    }
  };

  // Extraire les campagnes uniques du breakdown
  const getUniqueCampaigns = (breakdown) => {
    if (!breakdown || breakdown.length === 0) return [];
    const campaignMap = new Map();
    breakdown.forEach(b => {
      if (b.campaignId && !campaignMap.has(b.campaignId)) {
        campaignMap.set(b.campaignId, {
          id: b.campaignId,
          name: b.campaignName,
          type: b.campaignType
        });
      }
    });
    return Array.from(campaignMap.values());
  };

  // Extraire les groupes d'annonces uniques du breakdown
  const getUniqueAdGroups = (breakdown) => {
    if (!breakdown || breakdown.length === 0) return [];
    const adGroupMap = new Map();
    breakdown.forEach(b => {
      if (b.adGroupId && !adGroupMap.has(b.adGroupId)) {
        adGroupMap.set(b.adGroupId, {
          id: b.adGroupId,
          name: b.adGroupName,
          campaignName: b.campaignName,
          campaignType: b.campaignType
        });
      }
    });
    return Array.from(adGroupMap.values());
  };

  // Gérer l'exclusion
  const handleExclude = async () => {
    if (!excludeTerm || !selectedAccount) return;

    setExcludeLoading(true);
    setExcludeError(null);
    setExcludeSuccess(null);

    try {
      let target = {};

      if (excludeTargetType === 'campaign') {
        if (excludeSelectedTargets.length === 0) {
          setExcludeError('Veuillez sélectionner au moins une campagne');
          setExcludeLoading(false);
          return;
        }
        target = { type: 'campaign', ids: excludeSelectedTargets };
      } else if (excludeTargetType === 'adGroup') {
        if (excludeSelectedTargets.length === 0) {
          setExcludeError('Veuillez sélectionner au moins un groupe d\'annonces');
          setExcludeLoading(false);
          return;
        }
        target = { type: 'adGroup', ids: excludeSelectedTargets };
      } else if (excludeTargetType === 'sharedList') {
        if (!selectedSharedList) {
          setExcludeError('Veuillez sélectionner une liste partagée');
          setExcludeLoading(false);
          return;
        }
        target = { type: 'sharedList', ids: [selectedSharedList] };
      } else if (excludeTargetType === 'newSharedList') {
        if (!newListName.trim()) {
          setExcludeError('Veuillez entrer un nom pour la nouvelle liste');
          setExcludeLoading(false);
          return;
        }
        target = { type: 'newSharedList', ids: [], newListName: newListName.trim() };
      }

      const termToExclude = editedExcludeTerm.trim();
      if (!termToExclude) {
        setExcludeError('Le terme à exclure ne peut pas être vide');
        setExcludeLoading(false);
        return;
      }

      // Vérifier la longueur (limite Microsoft Ads: 100 caractères)
      if (termToExclude.length > 100) {
        setExcludeError(`Le terme est trop long (${termToExclude.length} caractères). Maximum: 100 caractères.`);
        setExcludeLoading(false);
        return;
      }

      // Vérifier le nombre de mots (limite Microsoft Ads: 10 mots)
      const wordCount = termToExclude.split(/\s+/).filter(w => w.length > 0).length;
      if (wordCount > 10) {
        setExcludeError(`Le terme contient trop de mots (${wordCount} mots). Maximum: 10 mots.`);
        setExcludeLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}/api/microsoft-keyword-opportunities/exclude`, {
        clientId: selectedAccount,
        keywords: [termToExclude],
        matchType: excludeMatchType,
        target
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 35000 // 35 secondes (API REST v24 = plus rapide)
      });

      if (response.data.success) {
        setExcludeSuccess(`${termToExclude} a été exclu avec succès`);
        // Rafraîchir les données après quelques secondes
        setTimeout(() => {
          setExcludeTerm(null);
          fetchData();
        }, 2000);
      } else {
        let errorText = response.data.error || 'Erreur lors de l\'exclusion';
        // S'assurer que l'erreur est une string
        if (typeof errorText === 'object') {
          errorText = JSON.stringify(errorText);
        }
        setExcludeError(errorText);
      }
    } catch (err) {
      console.error('Erreur exclusion:', err);
      console.error('Erreur response data:', err.response?.data);
      let errorMsg = err.response?.data?.error || err.message || 'Erreur lors de l\'exclusion';
      // S'assurer que errorMsg est une string
      if (typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg);
      }
      let details = err.response?.data?.details;
      // Si details est un objet, le convertir en string
      if (details && typeof details === 'object') {
        details = JSON.stringify(details);
      }
      setExcludeError(details ? `${errorMsg}: ${details}` : errorMsg);
    } finally {
      setExcludeLoading(false);
    }
  };

  // Charger les listes partagées quand on change de type de cible
  useEffect(() => {
    if (excludeTargetType === 'sharedList' && sharedLists.length === 0) {
      loadSharedLists();
    }
  }, [excludeTargetType]);

  // Analyser les termes à exclure
  const analyzeNegatives = useMemo(() => {
    if (!searchTermsData.length) return [];

    const normalizeText = (str) => {
      if (!str) return '';
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[''`]/g, '')
        .toLowerCase();
    };

    // Set des négatifs existants
    const existingNegativesSet = new Set(
      existingNegatives.map(k => normalizeText(k.text))
    );

    // Traiter les données selon le mode (agrégé ou non)
    let processedData = [];

    if (settings.aggregateByCampaign) {
      // Mode agrégé par terme : regrouper par terme (ignore campaign)
      const aggregatedMap = new Map();

      searchTermsData.forEach(term => {
        const termText = term.searchTerm || term.term;
        if (!termText) return; // Skip terms without text
        const termNormalized = normalizeText(termText);

        if (!aggregatedMap.has(termNormalized)) {
          aggregatedMap.set(termNormalized, {
            searchTerm: termText,
            campaignType: null,
            campaignTypes: [],
            campaignName: null,
            campaigns: [],
            adGroupName: null,
            adGroups: [],
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            breakdown: [] // Collecter les détails par campagne/adgroup
          });
        }

        const agg = aggregatedMap.get(termNormalized);
        agg.impressions += term.impressions || 0;
        agg.clicks += term.clicks || 0;
        agg.cost += term.cost || 0;
        agg.conversions += term.conversions || 0;
        // Fusionner les types de campagne
        if (term.campaignTypes && Array.isArray(term.campaignTypes)) {
          term.campaignTypes.forEach(ct => {
            if (ct && !agg.campaignTypes.includes(ct)) agg.campaignTypes.push(ct);
          });
        } else if (term.campaignType && !agg.campaignTypes.includes(term.campaignType)) {
          agg.campaignTypes.push(term.campaignType);
        }
        // Fusionner les campagnes
        if (term.campaigns) {
          term.campaigns.forEach(c => {
            if (!agg.campaigns.includes(c)) agg.campaigns.push(c);
          });
        }
        if (term.campaignName && !agg.campaigns.includes(term.campaignName)) {
          agg.campaigns.push(term.campaignName);
        }
        // Fusionner les ad groups
        if (term.adGroups) {
          term.adGroups.forEach(ag => {
            if (!agg.adGroups.includes(ag)) agg.adGroups.push(ag);
          });
        }
        if (term.adGroupName && !agg.adGroups.includes(term.adGroupName)) {
          agg.adGroups.push(term.adGroupName);
        }
        // Utiliser le breakdown existant du backend s'il existe, sinon construire depuis les champs
        if (term.breakdown && term.breakdown.length > 0) {
          term.breakdown.forEach(b => {
            // Éviter les doublons basé sur campaignId + adGroupId
            const exists = agg.breakdown.some(
              existing => existing.campaignId === b.campaignId && existing.adGroupId === b.adGroupId
            );
            if (!exists) {
              agg.breakdown.push(b);
            }
          });
        } else if (term.campaignId || term.campaignName) {
          // Fallback: construire depuis les champs individuels
          const exists = agg.breakdown.some(
            existing => existing.campaignId === term.campaignId && existing.adGroupId === term.adGroupId
          );
          if (!exists) {
            agg.breakdown.push({
              campaignId: term.campaignId,
              campaignName: term.campaignName,
              campaignType: term.campaignType,
              adGroupId: term.adGroupId,
              adGroupName: term.adGroupName,
              impressions: term.impressions || 0,
              clicks: term.clicks || 0,
              cost: term.cost || 0,
              conversions: term.conversions || 0
            });
          }
        }
      });

      processedData = Array.from(aggregatedMap.values());
    } else {
      // Mode non agrégé : garder chaque ligne séparée (par campagne ou par type selon aggregateByCampaign)
      processedData = searchTermsData
        .filter(term => term.searchTerm || term.term) // Skip terms without text
        .map(term => ({
        ...term,
        searchTerm: term.searchTerm || term.term,
        campaignTypes: term.campaignTypes || (term.campaignType ? [term.campaignType] : []),
        campaigns: term.campaigns || (term.campaignName ? [term.campaignName] : []),
        adGroups: term.adGroups || (term.adGroupName ? [term.adGroupName] : []),
        // Utiliser le breakdown du backend s'il existe, sinon construire depuis les champs
        breakdown: (term.breakdown && term.breakdown.length > 0) ? term.breakdown : [{
          campaignId: term.campaignId,
          campaignName: term.campaignName,
          campaignType: (term.campaignTypes && term.campaignTypes[0]) || term.campaignType || null,
          adGroupId: term.adGroupId,
          adGroupName: term.adGroupName,
          impressions: term.impressions || 0,
          clicks: term.clicks || 0,
          cost: term.cost || 0,
          conversions: term.conversions || 0
        }]
      }));
    }

    // Filtrer et calculer les recommandations
    const recommendations = [];

    processedData.forEach(term => {
      const termNormalized = normalizeText(term.searchTerm);

      // Ignorer si déjà en négatif
      if (existingNegativesSet.has(termNormalized)) return;

      const clicks = term.clicks || 0;
      const conversions = term.conversions || 0;
      const impressions = term.impressions || 0;
      const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
      const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
      const cost = term.cost || 0;

      // Ne jamais recommander l'exclusion de termes qui convertissent
      if (conversions > 0) {
        return;
      }

      // Minimum 1 clic pour être considéré
      if (clicks < 1) {
        return;
      }

      // Calculer le tier
      let tier = 'LOW';
      if (clicks >= 100 || cost >= 100) {
        tier = 'CRITICAL';
      } else if (clicks >= 50 || cost >= 50) {
        tier = 'HIGH';
      } else if (clicks >= 25 || cost >= 25) {
        tier = 'MEDIUM';
      }

      recommendations.push({
        searchTerm: term.searchTerm,
        impressions,
        clicks,
        conversions,
        cost,
        ctr,
        conversionRate,
        tier,
        campaignTypes: term.campaignTypes || [],
        campaignName: term.campaignName || null,
        campaigns: term.campaigns || [],
        adGroupName: term.adGroupName || null,
        adGroups: term.adGroups || [],
        breakdown: term.breakdown || [] // Détails par campagne/adgroup pour le modal
      });
    });

    return recommendations.sort((a, b) => b.clicks - a.clicks);
  }, [searchTermsData, existingNegatives, settings.aggregateByCampaign, settings.aggregateByCampaign]);

  // Filtrer par tier, minimum de clics et coût et type de campagne
  // Les deux filtres s'appliquent en ET (les deux conditions doivent être remplies)
  const filteredRecommendations = useMemo(() => {
    let result = analyzeNegatives.filter(r => {
      const meetsClicks = r.clicks >= settings.minClicks;
      const meetsCost = r.cost >= settings.minCost;
      return meetsClicks && meetsCost;
    });

    // Filtre par type de campagne
    if (settings.campaignType !== 'ALL') {
      result = result.filter(r => r.campaignTypes && r.campaignTypes.includes(settings.campaignType));
    }

    if (tierFilter) {
      if (tierFilter === 'CRITICAL_HIGH') {
        result = result.filter(r => r.tier === 'CRITICAL' || r.tier === 'HIGH');
      } else {
        result = result.filter(r => r.tier === tierFilter);
      }
    }

    // Appliquer le tri
    const tierOrder = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
    result = [...result].sort((a, b) => {
      let valA, valB;

      switch (sortColumn) {
        case 'searchTerm':
          return sortDirection === 'asc'
            ? a.searchTerm.localeCompare(b.searchTerm)
            : b.searchTerm.localeCompare(a.searchTerm);
        case 'tier':
          valA = tierOrder[a.tier] || 0;
          valB = tierOrder[b.tier] || 0;
          break;
        case 'impressions':
          valA = a.impressions;
          valB = b.impressions;
          break;
        case 'clicks':
          valA = a.clicks;
          valB = b.clicks;
          break;
        case 'ctr':
          valA = a.ctr;
          valB = b.ctr;
          break;
        case 'conversions':
          valA = a.conversions;
          valB = b.conversions;
          break;
        case 'conversionRate':
          valA = a.conversionRate;
          valB = b.conversionRate;
          break;
        case 'cost':
          valA = a.cost;
          valB = b.cost;
          break;
        default:
          valA = a.clicks;
          valB = b.clicks;
      }

      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });

    return result;
  }, [analyzeNegatives, tierFilter, settings.minClicks, settings.minCost, settings.campaignType, sortColumn, sortDirection]);

  // Appliquer la pagination aux résultats filtrés
  const paginatedRecommendations = useMemo(() => {
    return filteredRecommendations.slice(0, displayedCount);
  }, [filteredRecommendations, displayedCount]);

  // Calculer s'il reste des éléments à charger
  const hasMoreItems = filteredRecommendations.length > displayedCount;
  const remainingItems = filteredRecommendations.length - displayedCount;

  const handleLoadMore = () => {
    setDisplayedCount(prev => prev + ITEMS_PER_PAGE);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Statistiques (applique les mêmes filtres que le tableau, sauf tierFilter)
  const stats = useMemo(() => {
    let all = analyzeNegatives.filter(r => {
      const meetsClicks = r.clicks >= settings.minClicks;
      const meetsCost = r.cost >= settings.minCost;
      return meetsClicks && meetsCost;
    });

    // Appliquer le filtre par type de campagne (comme le tableau)
    if (settings.campaignType !== 'ALL') {
      all = all.filter(r => r.campaignTypes && r.campaignTypes.includes(settings.campaignType));
    }

    const critical = all.filter(r => r.tier === 'CRITICAL').length;
    const high = all.filter(r => r.tier === 'HIGH').length;
    const totalClicks = all.reduce((sum, r) => sum + r.clicks, 0);
    const totalCost = all.reduce((sum, r) => sum + r.cost, 0);

    return {
      total: all.length,
      critical,
      high,
      criticalHigh: critical + high,
      totalClicks,
      totalCost
    };
  }, [analyzeNegatives, settings.minClicks, settings.minCost, settings.campaignType]);

  const formatNumber = (num) => new Intl.NumberFormat('fr-FR').format(num);
  const formatCurrency = (num) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);

  const getCampaignTypeBadges = (campaignTypes) => {
    if (!campaignTypes || campaignTypes.length === 0) return null;

    const styles = {
      SEARCH: { bg: '#dbeafe', color: '#1e40af' },
      SHOPPING: { bg: '#fef3c7', color: '#92400e' },
      PMAX: { bg: '#e0e7ff', color: '#3730a3' }
    };

    return (
      <span style={{ display: 'inline-flex', gap: '4px', marginLeft: '8px' }}>
        {campaignTypes.map(type => (
          <span
            key={type}
            style={{
              display: 'inline-flex',
              padding: '2px 6px',
              fontSize: '10px',
              fontWeight: '500',
              borderRadius: '4px',
              backgroundColor: styles[type]?.bg || '#f3f4f6',
              color: styles[type]?.color || '#374151',
              textTransform: 'uppercase'
            }}
          >
            {type}
          </span>
        ))}
      </span>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ width: '100%', padding: '0' }}>
        {/* En-tête */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                margin: '0 0 8px 0'
              }}>
                <Ban style={{ marginRight: '8px', height: '24px', width: '24px', color: '#dc2626' }} />
                Détecteur de Termes de Recherche à Exclure
              </h2>
              <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
                Identifiez les termes de recherche qui gaspillent votre budget
              </p>
            </div>
            {selectedAccount && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  border: hasCustomSettings && !showFilters ? '1px solid #16a34a' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: showFilters ? '#f3f4f6' : hasCustomSettings ? '#f0fdf4' : 'white',
                  color: hasCustomSettings && !showFilters ? '#166534' : '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <Settings style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                {hasCustomSettings ? 'Paramètres personnalisés' : 'Paramètres'}
              </button>
            )}
          </div>
        </div>

        {/* Sélecteur de compte */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#111827', margin: '0 0 12px 0' }}>
            Compte Microsoft Ads
          </h3>

          {loadingAccounts ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #dc2626',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Chargement des comptes...</p>
            </div>
          ) : accounts.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
              Aucun compte Microsoft Ads trouvé
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', minWidth: '100px' }}>
                Sélectionner :
              </label>
              <select
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  // Réinitialiser les données et filtres lors du changement de compte
                  setSearchTermsData([]);
                  setExistingNegatives([]);
                  setTierFilter(null);
                  // Mettre à jour l'URL avec le compte sélectionné
                  if (updateUrlWithAccount) updateUrlWithAccount(e.target.value || null);
                }}
                style={{
                  flex: '1',
                  minWidth: '300px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">-- Choisir un compte --</option>
                {accounts.map(account => {
                  const accountId = account.sourceId || account.id;
                  const accountName = account.name || account.displayName;
                  // Éviter de dupliquer l'ID si déjà présent dans le nom
                  const displayName = accountName.includes(accountId) ? accountName : `${accountName} (${accountId})`;
                  return (
                    <option key={accountId} value={accountId}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>

        {/* Écran de chargement */}
        {selectedAccount && loading && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '60px 24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #dc2626',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px'
            }}></div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
              Analyse en cours...
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
              Identification des termes de recherche non performants
            </p>
          </div>
        )}

        {/* Statistiques */}
        {selectedAccount && !loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '8px', background: '#fee2e2', borderRadius: '8px', marginRight: '16px' }}>
                  <Ban style={{ height: '24px', width: '24px', color: '#dc2626' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 4px 0' }}>
                    Termes à exclure
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '600', color: '#111827', margin: '0' }}>
                    {formatNumber(stats.total)}
                  </p>
                </div>
              </div>
            </div>

            <div
              onClick={() => setTierFilter(tierFilter === 'CRITICAL_HIGH' ? null : 'CRITICAL_HIGH')}
              style={{
                background: tierFilter === 'CRITICAL_HIGH' ? '#fee2e2' : 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: tierFilter === 'CRITICAL_HIGH' ? '2px solid #dc2626' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '8px', background: '#ffedd5', borderRadius: '8px', marginRight: '16px' }}>
                  <AlertTriangle style={{ height: '24px', width: '24px', color: '#ea580c' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 4px 0' }}>
                    Priorité haute {tierFilter === 'CRITICAL_HIGH' && <span style={{ color: '#dc2626', fontSize: '12px' }}>(filtré)</span>}
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '600', color: '#111827', margin: '0' }}>
                    {formatNumber(stats.criticalHigh)}
                  </p>
                </div>
              </div>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '8px', background: '#dbeafe', borderRadius: '8px', marginRight: '16px' }}>
                  <MousePointer style={{ height: '24px', width: '24px', color: '#333333' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 4px 0' }}>
                    Clics gaspillés
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '600', color: '#111827', margin: '0' }}>
                    {formatNumber(stats.totalClicks)}
                  </p>
                </div>
              </div>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '8px', background: '#fee2e2', borderRadius: '8px', marginRight: '16px' }}>
                  <TrendingDown style={{ height: '24px', width: '24px', color: '#dc2626' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 4px 0' }}>
                    Budget gaspillé
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '600', color: '#111827', margin: '0' }}>
                    {formatCurrency(stats.totalCost)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Panneau de filtres */}
        {showFilters && !loading && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#111827', margin: '0 0 16px 0' }}>
              Paramètres d'analyse
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  Période d'analyse
                </label>
                <select
                  value={settings.period}
                  onChange={(e) => setSettings({ ...settings, period: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="LAST_30_DAYS">30 derniers jours</option>
                  <option value="LAST_90_DAYS">90 derniers jours</option>
                  <option value="LAST_180_DAYS">180 derniers jours</option>
                  <option value="LAST_365_DAYS">365 derniers jours</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  Type de campagne
                </label>
                <select
                  value={settings.campaignType}
                  onChange={(e) => setSettings({ ...settings, campaignType: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="ALL">Toutes les campagnes</option>
                  <option value="SEARCH">Search uniquement</option>
                  <option value="SHOPPING">Shopping uniquement</option>
                  <option value="PMAX">Performance Max uniquement</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  Clics minimum
                </label>
                <input
                  type="number"
                  value={settings.minClicks}
                  onChange={(e) => setSettings({ ...settings, minClicks: parseInt(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  min="0"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  Coût minimum (€)
                </label>
                <input
                  type="number"
                  value={settings.minCost}
                  onChange={(e) => setSettings({ ...settings, minCost: parseFloat(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  min="0"
                  step="1"
                />
              </div>

              <div style={{ paddingTop: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings.aggregateByCampaign}
                    onChange={(e) => setSettings({ ...settings, aggregateByCampaign: e.target.checked })}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Agréger les données par terme
                  </span>
                </label>
              </div>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { fetchData(); setShowFilters(false); }}
                style={{
                  padding: '8px 16px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.background = '#b91c1c'}
                onMouseLeave={(e) => e.target.style.background = '#dc2626'}
              >
                Sauvegarder les paramètres
              </button>
            </div>
          </div>
        )}

        {/* Tableau des recommandations */}
        {selectedAccount && !loading && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#111827', margin: '0' }}>
                Termes à exclure
                <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '8px' }}>
                  ({formatNumber(paginatedRecommendations.length)} affichés sur {formatNumber(filteredRecommendations.length)}{tierFilter ? ` / ${formatNumber(stats.total)} total` : ''})
                </span>
              </h3>
              {tierFilter && (
                <button
                  onClick={() => setTierFilter(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Réinitialiser le filtre
                </button>
              )}
            </div>

            {filteredRecommendations.length > 0 ? (
              <div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                  <thead style={{ backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th
                        onClick={() => handleSort('searchTerm')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: sortColumn === 'searchTerm' ? '#dc2626' : '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          userSelect: 'none',
                          minWidth: '200px'
                        }}
                      >
                        Terme de recherche {sortColumn === 'searchTerm' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('impressions')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: sortColumn === 'impressions' ? '#dc2626' : '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Impressions {sortColumn === 'impressions' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('ctr')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: sortColumn === 'ctr' ? '#dc2626' : '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        CTR {sortColumn === 'ctr' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('clicks')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: sortColumn === 'clicks' ? '#dc2626' : '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Clics {sortColumn === 'clicks' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('cost')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: sortColumn === 'cost' ? '#dc2626' : '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Coût {sortColumn === 'cost' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('conversionRate')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: sortColumn === 'conversionRate' ? '#dc2626' : '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Taux conv. {sortColumn === 'conversionRate' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('conversions')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: sortColumn === 'conversions' ? '#dc2626' : '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Conv. {sortColumn === 'conversions' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        CPA
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Valeur conv.
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                                                    letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        COS
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #e5e7eb',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: 'white' }}>
                    {paginatedRecommendations.map((item, index) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                          borderBottom: '1px solid #e5e7eb'
                        }}
                      >
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#111827', maxWidth: '350px', wordBreak: 'break-word' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px' }}>
                              <span
                                onClick={() => setSelectedTerm(item)}
                                style={{
                                  lineHeight: '1.4',
                                  cursor: 'pointer',
                                  color: '#333333',
                                  textDecoration: 'underline',
                                  textDecorationColor: 'transparent',
                                  transition: 'text-decoration-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.textDecorationColor = '#333333'}
                                onMouseLeave={(e) => e.target.style.textDecorationColor = 'transparent'}
                              >
                                {item.searchTerm}
                              </span>
                              {getCampaignTypeBadges(item.campaignTypes)}
                            </span>
                            {/* Afficher le nom de la campagne si non agrégé par campagne */}
                            {!settings.aggregateByCampaign && item.campaignName && (
                              <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '400' }}>
                                {item.campaignName}
                              </span>
                            )}
                            {/* Afficher la liste des campagnes si agrégé */}
                            {settings.aggregateByCampaign && item.campaigns && item.campaigns.length > 0 && (
                              <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '400' }}>
                                {item.campaigns.length === 1 ? item.campaigns[0] : `${item.campaigns.length} campagnes`}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {formatNumber(item.impressions)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {item.ctr.toFixed(2)}%
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {formatNumber(item.clicks)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {formatCurrency(item.cost)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {item.conversionRate.toFixed(2)}%
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {item.conversions % 1 === 0 ? item.conversions : item.conversions.toFixed(1)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {item.conversions > 0 ? formatCurrency(item.cost / item.conversions) : '-'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          {formatCurrency(0)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                          -
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openExcludeModal(item);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                            title="Exclure ce terme"
                          >
                            <MinusCircle size={14} />
                            Exclure
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bouton Charger plus */}
              {hasMoreItems && (
                <div style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <button
                    onClick={handleLoadMore}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#2563eb'}
                    onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                  >
                    Charger plus
                  </button>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    {formatNumber(remainingItems)} terme{remainingItems > 1 ? 's' : ''} restant{remainingItems > 1 ? 's' : ''}
                  </span>
                </div>
              )}
              </div>
            ) : (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <Ban style={{ margin: '0 auto 16px auto', height: '48px', width: '48px', color: '#9ca3af' }} />
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500', color: '#111827' }}>
                  Aucun terme à exclure détecté
                </h3>
                <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
                  Ajustez les filtres ou la période d'analyse
                </p>
              </div>
            )}
          </div>
        )}

        {/* Modal de détails par campagne/adgroup - rendu via Portal pour être au-dessus de tout */}
        {selectedTerm && createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
            onClick={() => setSelectedTerm(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '900px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* En-tête du modal */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    Détails du terme de recherche
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: '#f3f4f6',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    {selectedTerm.searchTerm}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTerm(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    color: '#6b7280'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Résumé global */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Impressions</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{formatNumber(selectedTerm.impressions)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Clics</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{formatNumber(selectedTerm.clicks)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Coût</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{formatCurrency(selectedTerm.cost)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Conv.</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{selectedTerm.conversions}</div>
                </div>
              </div>

              {/* Tableau des campagnes/adgroups */}
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                Répartition par campagne / groupe d'annonces
              </h4>

              {selectedTerm.breakdown && selectedTerm.breakdown.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Campagne
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Groupe d'annonces
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Type
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Impr.
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Clics
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Coût
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Conv.
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTerm.breakdown.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '10px 12px', fontSize: '13px', color: '#111827', maxWidth: '200px', wordBreak: 'break-word' }}>
                            {row.campaignName || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', maxWidth: '180px', wordBreak: 'break-word' }}>
                            {row.adGroupName || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {getCampaignTypeBadges([row.campaignType])}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {formatNumber(row.impressions)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {formatNumber(row.clicks)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {formatCurrency(row.cost)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {row.conversions}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  Aucun détail disponible pour ce terme.
                </div>
              )}

              {/* Bouton fermer */}
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSelectedTerm(null)}
                  style={{
                    padding: '8px 16px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                  onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal d'exclusion - rendu via Portal pour être au-dessus de tout */}
        {excludeTerm && createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999
            }}
            onClick={() => !excludeLoading && setExcludeTerm(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
            >
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              {/* En-tête du modal */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                userSelect: 'none'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MinusCircle size={20} style={{ color: '#dc2626' }} />
                  Exclure le terme
                </h3>
                <button
                  onClick={() => !excludeLoading && setExcludeTerm(null)}
                  disabled={excludeLoading}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: excludeLoading ? 'not-allowed' : 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    color: '#6b7280',
                    opacity: excludeLoading ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => !excludeLoading && (e.target.style.backgroundColor = '#f3f4f6')}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Messages de succès/erreur */}
              {excludeSuccess && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#dcfce7',
                  border: '1px solid #22c55e',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Check size={18} style={{ color: '#16a34a' }} />
                  <span style={{ color: '#166534', fontSize: '14px' }}>{excludeSuccess}</span>
                </div>
              )}

              {excludeError && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={18} style={{ color: '#dc2626' }} />
                  <span style={{ color: '#991b1b', fontSize: '14px' }}>{excludeError}</span>
                </div>
              )}

              {!excludeSuccess && (
                <>
                  {/* Terme à exclure (éditable) */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      Terme à exclure
                    </label>
                    <input
                      type="text"
                      value={editedExcludeTerm}
                      onChange={(e) => setEditedExcludeTerm(e.target.value)}
                      disabled={excludeLoading}
                      autoFocus={false}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: (editedExcludeTerm.length > 80 || editedExcludeTerm.split(/\s+/).filter(w => w.length > 0).length > 10)
                          ? '2px solid #dc2626'
                          : '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: excludeLoading ? '#f9fafb' : 'white',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '4px',
                      fontSize: '12px'
                    }}>
                      <span style={{ color: '#6b7280' }}>
                        Vous pouvez modifier le terme avant de l'exclure
                      </span>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <span style={{
                          color: editedExcludeTerm.split(/\s+/).filter(w => w.length > 0).length > 10 ? '#dc2626' : '#6b7280',
                          fontWeight: editedExcludeTerm.split(/\s+/).filter(w => w.length > 0).length > 10 ? '600' : '400'
                        }}>
                          {editedExcludeTerm.split(/\s+/).filter(w => w.length > 0).length}/10 mots
                        </span>
                        <span style={{
                          color: editedExcludeTerm.length > 80 ? '#dc2626' : '#6b7280',
                          fontWeight: editedExcludeTerm.length > 80 ? '600' : '400'
                        }}>
                          {editedExcludeTerm.length}/80 car.
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Type de ciblage */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      Type de ciblage
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {['EXACT', 'PHRASE', 'BROAD'].map(type => (
                        <button
                          key={type}
                          onClick={() => setExcludeMatchType(type)}
                          style={{
                            padding: '8px 16px',
                            border: excludeMatchType === type ? '2px solid #dc2626' : '1px solid #d1d5db',
                            borderRadius: '6px',
                            background: excludeMatchType === type ? '#fee2e2' : 'white',
                            color: excludeMatchType === type ? '#991b1b' : '#374151',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          {type === 'EXACT' && 'Exact'}
                          {type === 'PHRASE' && 'Expression'}
                          {type === 'BROAD' && 'Large'}
                        </button>
                      ))}
                      <span style={{
                        marginLeft: '8px',
                        padding: '6px 12px',
                        backgroundColor: '#fee2e2',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#991b1b'
                      }}>
                        {excludeMatchType === 'EXACT' && `[${editedExcludeTerm}]`}
                        {excludeMatchType === 'PHRASE' && `"${editedExcludeTerm}"`}
                        {excludeMatchType === 'BROAD' && editedExcludeTerm}
                      </span>
                    </div>
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                      {excludeMatchType === 'EXACT' && 'Exclut uniquement ce terme exact'}
                      {excludeMatchType === 'PHRASE' && 'Exclut les requêtes contenant cette expression'}
                      {excludeMatchType === 'BROAD' && 'Exclut les requêtes contenant tous ces mots'}
                    </p>
                  </div>

                  {/* Où exclure */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      Où exclure ?
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { value: 'campaign', label: 'Campagnes', icon: '📊' },
                        { value: 'adGroup', label: 'Groupes d\'annonces', icon: '📁' },
                        { value: 'sharedList', label: 'Liste partagée existante', icon: '📋' },
                        { value: 'newSharedList', label: 'Nouvelle liste partagée', icon: '➕' }
                      ].map(option => (
                        <label
                          key={option.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            border: excludeTargetType === option.value ? '2px solid #dc2626' : '1px solid #e5e7eb',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: excludeTargetType === option.value ? '#fef2f2' : 'white'
                          }}
                        >
                          <input
                            type="radio"
                            name="targetType"
                            value={option.value}
                            checked={excludeTargetType === option.value}
                            onChange={(e) => {
                              setExcludeTargetType(e.target.value);
                              setExcludeSelectedTargets([]);
                              // Pré-sélectionner les cibles si campagne ou adGroup
                              if (e.target.value === 'campaign' && excludeTerm.breakdown) {
                                const ids = [...new Set(excludeTerm.breakdown.map(b => b.campaignId).filter(Boolean))];
                                setExcludeSelectedTargets(ids);
                              } else if (e.target.value === 'adGroup' && excludeTerm.breakdown) {
                                const ids = [...new Set(excludeTerm.breakdown.map(b => b.adGroupId).filter(Boolean))];
                                setExcludeSelectedTargets(ids);
                              }
                            }}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span style={{ fontSize: '14px', color: '#374151' }}>
                            {option.icon} {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Sélection des cibles - Campagnes */}
                  {excludeTargetType === 'campaign' && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Sélectionner les campagnes
                      </label>
                      {excludeTerm.breakdown && excludeTerm.breakdown.length > 0 ? (
                        <div style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '8px'
                        }}>
                          {getUniqueCampaigns(excludeTerm.breakdown).map(campaign => (
                            <label
                              key={campaign.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                background: excludeSelectedTargets.includes(campaign.id) ? '#f3f4f6' : 'transparent'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={excludeSelectedTargets.includes(campaign.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setExcludeSelectedTargets([...excludeSelectedTargets, campaign.id]);
                                  } else {
                                    setExcludeSelectedTargets(excludeSelectedTargets.filter(id => id !== campaign.id));
                                  }
                                }}
                                style={{ width: '16px', height: '16px' }}
                              />
                              <span style={{ fontSize: '13px', color: '#374151', flex: 1 }}>
                                {campaign.name}
                              </span>
                              {getCampaignTypeBadges([campaign.type])}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#fef3c7',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#92400e'
                        }}>
                          Aucune campagne disponible. Décochez "Agréger les données par campagnes" pour voir le détail par campagne.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sélection des cibles - Groupes d'annonces */}
                  {excludeTargetType === 'adGroup' && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Sélectionner les groupes d'annonces
                      </label>
                      {excludeTerm.breakdown && getUniqueAdGroups(excludeTerm.breakdown).length > 0 ? (
                        <div style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '8px'
                        }}>
                          {getUniqueAdGroups(excludeTerm.breakdown).map(adGroup => (
                            <label
                              key={adGroup.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                background: excludeSelectedTargets.includes(adGroup.id) ? '#f3f4f6' : 'transparent'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={excludeSelectedTargets.includes(adGroup.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setExcludeSelectedTargets([...excludeSelectedTargets, adGroup.id]);
                                  } else {
                                    setExcludeSelectedTargets(excludeSelectedTargets.filter(id => id !== adGroup.id));
                                  }
                                }}
                                style={{ width: '16px', height: '16px' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13px', color: '#374151' }}>
                                  {adGroup.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                  {adGroup.campaignName}
                                </div>
                              </div>
                              {getCampaignTypeBadges([adGroup.campaignType])}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#fef3c7',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#92400e'
                        }}>
                          Aucun groupe d'annonces disponible. Les campagnes Performance Max n'ont pas de groupes d'annonces modifiables.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sélection liste partagée existante */}
                  {excludeTargetType === 'sharedList' && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Sélectionner une liste partagée
                      </label>
                      {loadingSharedLists ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
                          <span style={{ marginLeft: '8px' }}>Chargement des listes...</span>
                        </div>
                      ) : sharedLists.length > 0 ? (
                        <select
                          value={selectedSharedList}
                          onChange={(e) => setSelectedSharedList(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            background: 'white'
                          }}
                        >
                          <option value="">-- Choisir une liste --</option>
                          {sharedLists.map(list => (
                            <option key={list.id} value={list.id}>
                              {list.name} ({list.memberCount || 0} mots-clés)
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#f3f4f6',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}>
                          Aucune liste partagée trouvée. Créez-en une nouvelle.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Nouvelle liste partagée */}
                  {excludeTargetType === 'newSharedList' && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Nom de la nouvelle liste
                      </label>
                      <input
                        type="text"
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        placeholder="Ex: Exclusions générales"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                        La liste sera créée et le terme y sera ajouté. Vous pourrez ensuite l'associer à vos campagnes depuis Microsoft Ads.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Boutons d'action */}
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={() => setExcludeTerm(null)}
                  disabled={excludeLoading}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: excludeLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: excludeLoading ? 0.5 : 1
                  }}
                >
                  {excludeSuccess ? 'Fermer' : 'Annuler'}
                </button>
                {!excludeSuccess && (
                  <button
                    onClick={handleExclude}
                    disabled={excludeLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      background: excludeLoading ? '#9ca3af' : '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: excludeLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {excludeLoading ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Exclusion en cours...
                      </>
                    ) : (
                      <>
                        <MinusCircle size={16} />
                        Exclure ce terme
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </>
  );
};

export default MicrosoftNegativeKeywordsTool;
